import { useEffect, useState } from 'react'

interface PhotoGridItem {
  id: string
  image: string
  title: string
  place: string
}

interface PhotoGridSectionProps {
  label: string
  title: string
  intro: string
  items: PhotoGridItem[]
}

interface LightboxItem {
  image: string
  title: string
  place: string
}

export default function PhotoGridSection({ label, title, intro, items }: PhotoGridSectionProps) {
  const [lightbox, setLightbox] = useState<LightboxItem | null>(null)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightbox(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    document.body.style.overflow = lightbox ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [lightbox])

  return (
    <section id="photo-story" className="w-full bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-primary-500" />
            <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">{label}</span>
            <span className="h-px w-10 bg-primary-500" />
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
            {title}
          </h2>
          <p className="mt-5 text-base text-foreground-600 leading-relaxed">{intro}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((item) => (
            <figure
              key={item.id}
              className="group relative rounded-lg overflow-hidden border border-background-200 aspect-[4/5]"
            >
              <img
                src={item.image}
                alt={`${item.title} — ${item.place} wedding photographed by Shabi Media`}
                title={`${item.title} | Shabi Media wedding photography, ${item.place}`}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-90" />
              <button
                type="button"
                aria-label={`View ${item.title} larger`}
                onClick={() => setLightbox({ image: item.image, title: item.title, place: item.place })}
                className="absolute inset-0 cursor-pointer"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 pointer-events-none">
                <span className="inline-block text-[10px] tracking-[0.2em] uppercase text-primary-300 mb-2">
                  {item.place}
                </span>
                <h3 className="font-heading text-lg md:text-xl text-background-50 leading-snug">{item.title}</h3>
              </figcaption>
              <span className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-black/40 text-background-50 opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-fullscreen-line text-base" />
              </span>
            </figure>
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 md:p-10"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center bg-background-50/10 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <figure className="max-w-4xl w-full max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.image}
              alt={lightbox.title}
              className="max-h-[72vh] w-auto max-w-full object-contain rounded-lg"
            />
            <figcaption className="mt-5 text-center">
              <span className="block text-[11px] tracking-[0.2em] uppercase text-primary-300 mb-2">
                {lightbox.place}
              </span>
              <span className="font-heading text-xl md:text-2xl text-background-50">{lightbox.title}</span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  )
}